import { useQuery } from '@tanstack/react-query'
import { RowDoubleClickedEvent } from 'ag-grid-enterprise'
import { CustomCellRendererProps } from 'ag-grid-react'
import { Edit, PlusCircle } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import {
  CustomAgGrid,
  CustomMenuItem,
  createCellRenderer,
  createColDefs,
} from '@fortem-monorepo/react-ag'
import { createContextMenu } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { Badge, Button } from '@fortem-monorepo/react-ui'
import { Audit, ICG2Guidelines } from '@fortem-monorepo/web-sdk'

import { audit } from '@/ag-grid/columns/audit/col-def'
import { useAuditMenuItems } from '@/ag-grid/context-menus/with-audit-info'
import { useUserContext } from '@/index'
import { useGuidelineDrawer } from '@/pages/funds/ic-guidelines-v2/components/GuidelineDrawerContext'
import { useFunds } from '@/react-query/queries/funds/funds'

export interface ManageGuidelinesTabProps {}
type GuidelineRow = ICG2Guidelines.Guideline & Audit.WithApprovalInfo

export const ManageGuidelinesTab: React.FC<ManageGuidelinesTabProps> = ({}) => {
  const { openCreate, openEdit } = useGuidelineDrawer()
  const { hasPermissions } = useUserContext()

  const canApprove = hasPermissions((permissions) => ({
    permissions: [permissions.APPROVE_IC_GUIDELINES],
  }))

  const api = useApi()

  const guidelinesQuery = useQuery({
    queryKey: ['ic-guidelines', 'guidelines'],
    queryFn: () => api.icGuidelines.v2.getGuidelines(),
  })

  // ---

  const auditMenuItems = useAuditMenuItems<GuidelineRow>({
    approve: { label: 'Approve Guideline', element_type: Audit.ELEMENT_TYPES.IC_GUIDELINE },
  })

  const contextMenu = createContextMenu<GuidelineRow>((params) => {
    const defaultItems = params.defaultItems ?? []
    const approveItem = auditMenuItems.approve(params, 'id')

    return [
      ...(approveItem && canApprove ? ([approveItem, 'separator'] satisfies CustomMenuItem[]) : []),
      {
        name: 'Edit Guideline',
        icon: renderToString(<Edit size={16} />),
        action: () => {
          openEdit(params.node?.data?.id!)
        },
      },
      // 'separator',
      // {
      //   name: 'Delete Guideline',
      //   icon: renderToString(<Trash2 size={16} />),
      //   cssClasses: ['hover:bg-red-100!', 'text-red-600!'],
      //   action: (params) => {
      //     const guideline_id = params.node?.data?.id
      //     if (guideline_id) {
      //       return confirmDeleteGuideline(() =>
      //         deleteGuidelineMutation.mutateAsync({ guideline_id }),
      //       )
      //     }
      //   },
      // },
      'separator',
      ...defaultItems,
    ]
  })

  return (
    <>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={'outline'} onClick={() => openCreate()}>
            Create Guideline
            <PlusCircle />
          </Button>
        </div>
      </HeaderConfigBar>
      <Widget className={'h-full'} exposed>
        <WidgetHeaderWithQuery title="All Guidelines" query={guidelinesQuery} />
        <WidgetContent loading={guidelinesQuery.isLoading}>
          <CustomAgGrid
            query={guidelinesQuery}
            domLayout={'normal'}
            onRowDoubleClicked={(params: RowDoubleClickedEvent<GuidelineRow>) =>
              openEdit(params.node?.data?.id!)
            }
            getContextMenuItems={contextMenu}
            columnDefs={coldefs()}
            rowData={guidelinesQuery.data}
          />
        </WidgetContent>
      </Widget>
    </>
  )
}

const coldefs = () => {
  return createColDefs<ICG2Guidelines.Guideline>([
    { field: 'id', headerName: 'ID' },
    audit,
    {
      field: 'is_active',
      headerName: 'Active',
      width: 90,
      cellRenderer: createCellRenderer([
        {
          check: (val) => !!val,
          component: (
            <Badge variant={'outline'} theme={'success'}>
              Active
            </Badge>
          ),
          field: 'is_active',
        },
        {
          check: (val) => !val,
          component: (
            <Badge variant={'outline'} theme={'destructive'}>
              Inactive
            </Badge>
          ),
          field: 'is_active',
        },
      ]),
    },
    { field: 'name' },
    { field: 'description' },
    { field: 'funds', cellRenderer: FundsRenderer, minWidth: 200 },
    { field: 'table' },
    { field: 'value_column' },
    { field: 'threshold_type', hide: true },
    { field: 'aggregation_function', hide: true },
    { field: 'identifiers', hide: true },
    { field: 'thresholds', hide: true },
  ])
}

export interface FundsRendererProps extends CustomCellRendererProps<ICG2Guidelines.Guideline> {}

export const FundsRenderer: React.FC<FundsRendererProps> = (params) => {
  const { data: funds } = useFunds()

  const idNameMap = funds.reduce(
    (prev, curr) => {
      const _new = { ...prev }
      if (!_new[curr.fund_id]) _new[curr.fund_id] = curr.fund_name
      return _new
    },
    {} as Record<number, string>,
  )

  const fundNames = (params.node.data?.funds ?? [])
    .map((d) => idNameMap[Number(d)])
    .filter((v) => v !== undefined)

  return (
    <div className={'flex items-center gap-1 h-full'}>
      {fundNames.map((fund) => (
        <Badge variant={'secondary'}>{fund}</Badge>
      ))}
    </div>
  )
}
